import React from "react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export type BadgeVariant = "success" | "warning" | "error" | "info" | "neutral" | "transparent" | "renewal" | "agent";
export type BadgeSize = "xs" | "sm" | "md" | "lg";

export interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: LucideIcon;
  className?: string;
}

const variantStyles = {
  success: "bg-green-100 text-green-800 border-green-200",
  warning: "bg-amber-100 text-amber-800 border-amber-200",
  error: "bg-red-100 text-red-800 border-red-200",
  info: "bg-blue-100 text-blue-800 border-blue-200",
  neutral: "bg-gray-100 text-gray-800 border-gray-200",
  transparent: "bg-transparent text-gray-700 border-transparent",
  renewal: "bg-purple-100 text-purple-800 border-purple-200",
  agent: "bg-violet-100 text-violet-800 border-violet-300",
};

const iconColorStyles = {
  success: "text-green-800",
  warning: "text-amber-800",
  error: "text-red-800",
  info: "text-blue-800",
  neutral: "text-gray-800",
  transparent: "text-gray-800",
  renewal: "text-purple-800",
  agent: "text-violet-800",
};

const sizeStyles = {
  xs: "px-1.5 py-0 text-xs leading-tight",
  sm: "px-2.5 py-0.5 text-sm",
  md: "px-3 py-1 text-base",
  lg: "px-3 py-1.5 text-lg",
};

const iconSizeStyles = {
  xs: "w-3 h-3",
  sm: "w-3.5 h-3.5",
  md: "w-4 h-4",
  lg: "w-4 h-4",
};

export function Badge({
  children,
  variant = "neutral",
  size = "md",
  icon: Icon,
  className = "",
}: BadgeProps) {
  const variantClass = variantStyles[variant];
  const sizeClass = sizeStyles[size];
  const iconSizeClass = Icon ? iconSizeStyles[size] : "";
  const iconColorClass = Icon ? iconColorStyles[variant] : "";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 font-normal rounded-full border whitespace-nowrap",
        variantClass,
        sizeClass,
        className,
      )}
    >
      {Icon && <Icon className={cn(iconSizeClass, iconColorClass)} />}
      {children}
    </span>
  );
}
